/**
 * The runner container's entrypoint wrapper, as a shell string.
 *
 * ## Why this exists at all
 *
 * IM-30 was written assuming the `qrvey-terraform` installer image would read
 * `QRVEY_*` environment variables, assume the deploy role itself, and write its
 * own `config.json`. Inspecting the image (`9.5-r-20260831-0793`) settled it: it
 * does none of those things.
 *
 * - It reads **no** `QRVEY_COMMAND` / `QRVEY_RUN_ID` / `QRVEY_VARIABLES` /
 *   `QRVEY_DEPLOY_ROLE_ARN` / `QRVEY_EXTERNAL_ID` / `QRVEY_STATE_BUCKET` /
 *   `QRVEY_LOCK_TABLE`. Its verb is argv; its inputs are a mounted config file.
 * - It contains no assume-role logic of any kind. `initialize.sh` fails hard
 *   unless it finds credentials in `account_config` *or* in the environment, and
 *   it does not fall back to the ambient credential chain — so the ECS task role
 *   alone is not enough.
 * - It never creates a config file; it only writes back to one that exists.
 *
 * So something inside the container has to assume the deploy role and lay down
 * the config before the installer runs, and this is that something. It is the
 * *only* change of shape: the security property IM-26 established is unchanged,
 * because the assume-role still happens under the runner's own identity, gated
 * by the grant condition AWS evaluates at that call. Neither Qrvey's control
 * plane nor this account's dispatcher ever holds customer credentials.
 *
 * It lives in the task definition's `EntryPoint`, not in a `command` override,
 * because ECS `containerOverrides` can override `command` but **not**
 * `entryPoint`. That is the better place regardless: the customer reads it in
 * the same template they already review, and no Qrvey-hosted artifact can be
 * swapped after they read it. It does not count against the dispatcher's 4096
 * character budget, which is CloudFormation's limit for inline *Lambda* code.
 *
 * ## The one-hour ceiling, which is not fixed here
 *
 * These credentials cannot outlive one hour. The task role is itself an assumed
 * role, so this is role chaining, which AWS caps at 3600 seconds regardless of
 * the deploy role's `MaxSessionDuration` (currently 14400, and unreachable from
 * here). Worse, the installer freezes whatever it is given into a Terraform var
 * file — `aws_access_key_id`, `aws_secret_access_key`, `aws_session_token` — so
 * the provider holds static credentials and no refresh is possible from outside
 * the image.
 *
 * An apply is 30–90 minutes. So a long one will fail partway through on expired
 * credentials, and no arrangement of this wrapper prevents it: the fix belongs
 * to the image, which would need to resolve credentials through a refreshing
 * profile (`credential_source = EcsContainer`) rather than baking them in.
 * Recorded here rather than worked around, because a silent workaround would
 * hide a failure that lands in a customer's account.
 */
import { COMPLETION_MARKERS } from '../runs/completion.js';
import { REDACTOR_SOURCE } from './redaction.js';
/**
 * The `case` arms that tell the wrapper what "finished" looks like.
 *
 * Generated from `COMPLETION_MARKERS` rather than written out here, so the
 * string the wrapper matches on and the string the nightly checks for are the
 * same string. A command with no entry gets no arm, which leaves `expect` empty
 * and turns the assertion off — see the wrapper's own note.
 */
const markerCases = Object.entries(COMPLETION_MARKERS)
    .map(([command, marker]) => `  ${command}) expect='${marker}' ;;`)
    .join('\n');
export const RUNNER_WRAPPER = `set -euo pipefail

# Overridable only so the tests can execute this script against a temporary
# directory; in the image both paths are /app.
app="\${QRVEY_APP_DIR:-/app}"

: "\${QRVEY_DEPLOY_ROLE_ARN:?}" "\${QRVEY_STATE_BUCKET:?}"
: "\${QRVEY_LOCK_TABLE:?}" "\${QRVEY_REGION:?}" "\${QRVEY_RUN_ID:?}" "\${QRVEY_VARIABLES:?}"

# The registry credentials this account already holds (IM-118).
#
# Read here rather than carried on the run. The same ACR token authenticates two
# different pulls -- ECS fetching the installer image, and the chart fetching
# Qrvey's product images inside the cluster -- and only the first was provisioned
# in this account, so the second had to be typed into a form for every single
# run. It is the one variable 'splitSecretVariables' can never remember, because
# it is stripped before the run record is written.
#
# Three things about where this sits:
#
#   * BEFORE the assume-role below, deliberately. This reads with the runner's
#     own identity, whose policy allows exactly this one secret; afterwards the
#     environment carries deploy-role credentials, and the read would be made by
#     a role that has no business holding that permission.
#   * A value sent with the run still wins. The account's copy is a default, not
#     an override -- someone testing a new token must be able to pass one.
#   * Never echoed. The value goes from the CLI into a variable and then into
#     config.json, and the redactor would mask it in the log either way.
if [ -n "\${QRVEY_REGISTRY_SECRET_ARN:-}" ]; then
  sent_key=$(printf '%s' "$QRVEY_VARIABLES" | jq -r '.registry_key // empty')
  if [ -z "$sent_key" ]; then
    registry=$(aws secretsmanager get-secret-value \
      --secret-id "$QRVEY_REGISTRY_SECRET_ARN" \
      --query SecretString --output text)
    # The shape ECS requires of repositoryCredentials, so it is the shape this
    # reads. A secret that is not that shape yields empty strings rather than
    # nonsense, and the installer then refuses for a named missing variable.
    QRVEY_VARIABLES=$(printf '%s' "$QRVEY_VARIABLES" | jq \
      --arg user "$(printf '%s' "$registry" | jq -r '.username // empty')" \
      --arg key "$(printf '%s' "$registry" | jq -r '.password // empty')" \
      '. + (if $key == "" then {} else {registry_key: $key} end)
         + (if $user == "" or (.registry_user // "") != "" then {} else {registry_user: $user} end)')
    unset registry
  fi
fi

# Assumed here, by the task's own role, under the grant condition. Capped at one
# hour by role chaining -- see the note above; this is not a value to raise.
#
# No --external-id since bootstrap v5. The deploy role trusts one role ARN in
# this same account and nothing outside it, so there was no confused deputy for
# a shared secret to guard against -- only a value to keep in step across three
# templates. The grant condition is what gates this call, and still does.
#
# Assigned before export: 'export x=$(cmd)' hides a failing substitution from
# 'set -e', because export itself succeeds.
creds=$(aws sts assume-role \\
  --role-arn "$QRVEY_DEPLOY_ROLE_ARN" \\
  --role-session-name "qim-$QRVEY_RUN_ID" \\
  --duration-seconds 3600 \\
  --query Credentials --output json)

AWS_ACCESS_KEY_ID=$(printf '%s' "$creds" | jq -r .AccessKeyId)
AWS_SECRET_ACCESS_KEY=$(printf '%s' "$creds" | jq -r .SecretAccessKey)
AWS_SESSION_TOKEN=$(printf '%s' "$creds" | jq -r .SessionToken)
export AWS_ACCESS_KEY_ID AWS_SECRET_ACCESS_KEY AWS_SESSION_TOKEN

# Where this run keeps its state.
#
# Sent with the dispatch since IM-139, and derived from the deployment id only
# when it is not. Both cases are real and neither is the odd one out: an
# instance created before that carries no key and lives at the convention, an
# instance created without a deployment id has a key the control plane minted,
# and an adopted instance's state is wherever the installer first put it.
#
# Deriving it unconditionally is what made a deployment id mandatory here, and
# the installer never required one -- variables.tf defaults it to "" and
# generates its own from random_string.id.
deployment_id=$(printf '%s' "$QRVEY_VARIABLES" | jq -r '.deployment_id // empty')
state_key="\${QRVEY_STATE_KEY:-}"
if [ -z "$state_key" ]; then
  if [ -z "$deployment_id" ]; then
    echo "runner: neither QRVEY_STATE_KEY nor variables.deployment_id was supplied" >&2
    exit 2
  fi
  state_key="$deployment_id/state.tfstate"
fi

# The bucket must belong to this account, whoever named it.
#
# The task definition fixes the bucket precisely so a caller cannot point a run
# at one it chose -- and an adopted instance's state is in a bucket this stack
# did not create, so the dispatch has to be able to name one. The guard keeps
# the property that mattered: a state file holds every secret Terraform touched,
# and the deploy role is account-wide administrator, so an unchecked name is an
# exfiltration path out of the account. Ownership is the check, not the name.
account_id=$(aws sts get-caller-identity --query Account --output text)
if ! aws s3api head-bucket --bucket "$QRVEY_STATE_BUCKET" \
  --expected-bucket-owner "$account_id" >/dev/null 2>&1; then
  echo "runner: state bucket $QRVEY_STATE_BUCKET is not owned by account $account_id" >&2
  exit 2
fi

# account_config carries no credentials: the installer takes those from the
# environment, and this file is copied back to disk by the image on exit.
printf '%s' "$QRVEY_VARIABLES" | jq \\
  --arg region "$QRVEY_REGION" \\
  --arg bucket "$QRVEY_STATE_BUCKET" \\
  --arg key "$state_key" \\
  '{account_config: {region: $region, bucket: $bucket, key: $key}, variables: .}' \\
  > "$app/config.json"

# The installer generates its backend config with bucket, region and key only,
# and drops account_config.dynamodb_table entirely -- so without this the state
# is never locked, which bootstraps cleanly and then corrupts under a second
# concurrent run. Terraform merges this flag with the installer's own.
export TF_CLI_ARGS_init="-backend-config=dynamodb_table=$QRVEY_LOCK_TABLE"

# Redaction, written out here so the customer can read in this template exactly
# what is masked before anything is logged. See redaction.ts.
cat > "$app/qrvey-redact.py" <<'QRVEY_REDACTOR'
${REDACTOR_SOURCE}QRVEY_REDACTOR

# Fail closed. If a seeded secret survives the rules, nothing runs at all: a run
# that fails is recoverable, and one that has already written unredacted logs is
# not.
python3 "$app/qrvey-redact.py" --self-test

# NOT exec, and not a plain redirect: the installer's output -- both streams --
# passes through the redactor before it reaches the log driver, so nothing
# unredacted is ever written, in this account or anywhere downstream. If the
# redactor dies the pipe breaks and the installer dies with it, which is the
# same fail-closed answer.
#
# set +e around it because the installer's exit status is the run's result and
# 'set -e' would abort before it could be read. PIPESTATUS[0] is that status;
# the pipeline's own is the redactor's.
# What this command prints when it genuinely finished. Empty for a verb with no
# known marker (shell, validate, adopt, recover), which turns the check below
# off rather than failing a command we have no expectation for.
expect=''
case "\${1:-}" in
${markerCases}
esac

set +e
"$app/entrypoint.sh" "$@" 2>&1 | python3 -u "$app/qrvey-redact.py" | awk -v m="$expect" '
  { print; fflush() }
  m != "" && index($0, m) { seen = 1 }
  END { exit((m == "" || seen) ? 0 : 3) }
'
# Captured in one assignment. PIPESTATUS is rebuilt by every command, including
# the assignment itself, so reading [0] and then [2] leaves the second read
# looking at a one-element array -- which under 'set -u' is an unbound variable
# and kills the run with 127 instead of reporting it.
statuses=(\${PIPESTATUS[@]})
installer_status=\${statuses[0]}
# awk, which passes every line through unchanged and reports only whether it
# ever saw the marker.
marker_status=\${statuses[2]}
set -e

# The installer's own failure always wins, and is reported unchanged: if it
# exited non-zero we already have the truth and the marker adds nothing.
if [ "$installer_status" -ne 0 ]; then
  exit "$installer_status"
fi

# IM-80. The installer exits 0 after Terraform fails -- measured on apply
# rmtvk9nhf-3r1plh, where 'Error: Kubernetes cluster unreachable' was followed by
# exitCode 0, the run recorded 'succeeded', and the console showed a live
# deployment whose URL returned 503. The exit code is the only thing the
# completion event carries, so a lie here becomes a lie everywhere downstream.
# The marker is the installer's own completion line; if it never printed, this
# run did not finish, whatever the exit code says.
if [ "$marker_status" -ne 0 ]; then
  # The wrapper's own stderr, outside the redacted pipeline -- safe only because
  # every value in it is a constant from this template, never installer output.
  echo "qrvey-wrapper: exited 0 but never printed [$expect] -- treating as failed (IM-80)" >&2
  exit 3
fi

exit 0
`;
//# sourceMappingURL=runner-wrapper.js.map